import React, { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Star, ShoppingBag, Heart, Truck, Leaf, ShieldCheck, Package } from 'lucide-react';
import AnnouncementBar from '../components/AnnouncementBar';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useProducts } from '../context/ProductsContext';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { byId, products } = useProducts();
  const { addItem } = useCart();
  const { has, toggle } = useWishlist();
  const { user, login } = useAuth();
  const { toast } = useToast();

  const [product, setProduct] = useState(byId(id));
  const [size, setSize] = useState(null);
  const [qty, setQty] = useState(1);

  React.useEffect(() => {
    let alive = true;
    if (!product) {
      axios.get(`${API}/products/${id}`).then(r => { if (alive) { setProduct(r.data); setSize(r.data.sizes[1] || r.data.sizes[0]); }}).catch(() => {});
    } else if (!size) {
      setSize(product.sizes[1] || product.sizes[0]);
    }
    return () => { alive = false; };
  }, [id, product, size]);

  const related = useMemo(() =>
    products.filter(p => p.id !== id && p.category === product?.category).slice(0, 4),
  [products, id, product]);

  if (!product) {
    return (
      <>
        <AnnouncementBar /><Header />
        <div className="max-w-[1400px] mx-auto px-4 py-24 text-center">
          <p className="text-lg" style={{ color: 'var(--gk-muted)' }}>Loading product...</p>
        </div>
        <Footer />
      </>
    );
  }

  const handleAdd = () => {
    for (let i = 0; i < qty; i++) addItem({ id: product.id, name: product.name, image: product.image, price: product.price }, size);
    toast({ title: 'Added to cart', description: `${product.name} · ${size} × ${qty}` });
  };

  const handleWishlist = async () => {
    if (!user) { toast({ title: 'Sign in first', description: 'Sign in to save your favourite spices.' }); return login(); }
    const r = await toggle(product.id);
    if (r.ok) toast({ title: r.has ? 'Added to wishlist' : 'Removed from wishlist' });
  };

  return (
    <>
      <AnnouncementBar />
      <Header />
      <main className="max-w-[1400px] mx-auto px-4 md:px-8 py-8">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm mb-6 link-underline" style={{ color: 'var(--gk-green-dark)' }}>
          <ArrowLeft size={16} /> Back
        </button>

        <div className="grid md:grid-cols-2 gap-10">
          <div className="rounded-2xl overflow-hidden" style={{ background: 'var(--gk-cream-dark)', boxShadow: '0 30px 60px -30px rgba(63,82,37,0.4)' }}>
            <img src={product.image} alt={product.name} className="w-full aspect-square object-cover" />
          </div>

          <div>
            {product.badge && <span className="chip mb-3" style={{ background: 'var(--gk-terracotta)', color: '#fff' }}>{product.badge}</span>}
            <h1 className="font-serif text-3xl md:text-4xl font-bold" style={{ color: 'var(--gk-green-dark)' }}>{product.name}</h1>
            <div className="flex items-center gap-1 mt-3">
              {[0,1,2,3,4].map(i => <Star key={i} size={16} fill="var(--gk-gold)" strokeWidth={0} />)}
              <span className="text-sm ml-2" style={{ color: 'var(--gk-muted)' }}>{product.reviews} reviews</span>
            </div>
            <div className="font-serif text-4xl font-bold mt-4" style={{ color: 'var(--gk-terracotta)' }}>₹{product.price}</div>
            <p className="mt-4 text-base leading-relaxed" style={{ color: 'var(--gk-ink)' }}>{product.description}</p>

            <div className="mt-6">
              <div className="text-xs uppercase tracking-widest mb-2" style={{ color: 'var(--gk-muted)' }}>Choose size</div>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map(s => (
                  <button key={s} onClick={() => setSize(s)} className="px-4 py-2 rounded-full text-sm font-medium transition-colors"
                    style={size === s
                      ? { background: 'var(--gk-green)', color: '#fff', border: '1px solid var(--gk-green)' }
                      : { background: 'transparent', color: 'var(--gk-ink)', border: '1px solid #ddd0b3' }}>{s}</button>
                ))}
              </div>
            </div>

            <div className="mt-5 flex items-center gap-4">
              <div className="text-xs uppercase tracking-widest" style={{ color: 'var(--gk-muted)' }}>Qty</div>
              <div className="inline-flex items-center gap-3 px-3 py-1.5 rounded-full" style={{ border: '1px solid #ddd0b3' }}>
                <button onClick={() => setQty(q => Math.max(1, q - 1))} className="w-6 h-6">−</button>
                <span className="w-6 text-center font-semibold">{qty}</span>
                <button onClick={() => setQty(q => q + 1)} className="w-6 h-6">+</button>
              </div>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={handleAdd} className="gk-btn-primary"><ShoppingBag size={16} /> Add to Cart</button>
              <button onClick={handleWishlist} className="gk-btn-outline inline-flex items-center gap-2">
                <Heart size={16} fill={has(product.id) ? 'var(--gk-terracotta)' : 'none'} style={{ color: 'var(--gk-terracotta)' }} />
                {has(product.id) ? 'Wishlisted' : 'Wishlist'}
              </button>
            </div>

            <div className="mt-8 grid grid-cols-2 gap-3">
              {[
                { icon: Truck, label: 'Free shipping over ₹499' },
                { icon: Leaf, label: '100% natural, no additives' },
                { icon: ShieldCheck, label: 'Freshly milled to order' },
                { icon: Package, label: 'Small-batch packaging' },
              ].map((f, i) => (
                <div key={i} className="flex items-center gap-2 p-3 rounded-lg" style={{ background: 'var(--gk-cream)' }}>
                  <f.icon size={16} style={{ color: 'var(--gk-green)' }} />
                  <span className="text-xs" style={{ color: 'var(--gk-ink)' }}>{f.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {related.length > 0 && (
          <div className="mt-20">
            <h2 className="font-serif text-2xl font-bold mb-6" style={{ color: 'var(--gk-green-dark)' }}>You may also like</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {related.map(p => (
                <a key={p.id} href={`/product/${p.id}`} className="product-card p-3 block">
                  <div className="aspect-square rounded-lg overflow-hidden mb-3" style={{ background: 'var(--gk-cream-dark)' }}>
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="font-serif text-sm font-semibold" style={{ color: 'var(--gk-ink)' }}>{p.name}</div>
                  <div className="font-bold mt-1" style={{ color: 'var(--gk-green-dark)' }}>₹{p.price}</div>
                </a>
              ))}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </>
  );
}
